import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "src/components/ui/Card";
import { Button } from "src/components/ui/Button";
import { Badge } from "src/components/ui/Badge";
import { Pencil } from "lucide-react";
import { Modal } from "src/components/ui/Modal";
import { FormInput } from "src/components/ui/FormInput";
import { StudentExtendedProfileData } from "src/pages/student/Profile";
import { ChipInput } from "src/components/ui/ChipInput";

interface HobbiesInterestsSectionProps {
  profileData: StudentExtendedProfileData;
  editData: Partial<StudentExtendedProfileData>;
  activeSectionEdit: string | null;
  setActiveSectionEdit: (section: string | null) => void;
  updateEditData: (field: string, value: any) => void;
  updateProfile: (data: Partial<StudentExtendedProfileData>) => Promise<void>;
}

const HobbiesInterestsSection: React.FC<HobbiesInterestsSectionProps> = ({
  profileData,
  editData,
  activeSectionEdit,
  setActiveSectionEdit,
  updateEditData,
  updateProfile
}) => {
  return (
    <>
      {/* Hobbies & Interests Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Hobbies & Interests</CardTitle>
          <button
            onClick={() => setActiveSectionEdit('hobbies')}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Edit Hobbies & Interests"
          >
            <Pencil className="h-4 w-4 text-gray-500" />
          </button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Hobbies:</h3>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                {profileData.hobbies.length > 0 ? (
                  profileData.hobbies.map((hobby, index) => (
                    <li key={index} className="text-sm">{hobby}</li>
                  ))
                ) : (
                  <p className="text-gray-500">No hobbies specified</p>
                )}
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500">Interests:</h3>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                {profileData.interests.length > 0 ? (
                  profileData.interests.map((interest, index) => (
                    <li key={index} className="text-sm">{interest}</li>
                  ))
                ) : (
                  <p className="text-gray-500">No interests specified</p>
                )}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hobbies & Interests Edit Modal */}
      {activeSectionEdit === 'hobbies' && (
        <Modal
          isOpen={activeSectionEdit === 'hobbies'}
          onClose={() => setActiveSectionEdit(null)}
          title="Edit Hobbies & Interests"
        >
          <div className="space-y-5">
            <ChipInput
              label="Hobbies"
              value={editData.hobbies || []}
              onChange={(chips) => {
                const values = Array.isArray(chips)
                  ? chips.map((chip) => typeof chip === 'string' ? chip : chip.value)
                  : [];
                updateEditData("hobbies", values);
              }}
              options={[]}
              allowCustom={true}
              multi={true}
              placeholder="Press Enter to add Hobbies"
              showDropdown={false}
            />
            <ChipInput
              label="Interests"
              value={editData.interests || []}
              onChange={(chips) => {
                const values = Array.isArray(chips)
                  ? chips.map((chip) => typeof chip === 'string' ? chip : chip.value)
                  : [];
                updateEditData("interests", values);
              }}
              options={[]}
              allowCustom={true}
              multi={true}
              placeholder="Press Enter to add Interests"
              showDropdown={false}
            />
          </div>
          <div className="flex flex-row w-full gap-2 mt-6 justify-between">
            <Button variant="outline" onClick={() => setActiveSectionEdit(null)} className="w-32">
              Cancel
            </Button>
            <Button onClick={() => {
              updateProfile(editData);
              setActiveSectionEdit(null);
            }} className="w-32">
              OK
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default HobbiesInterestsSection;
